import { AuthContext, useIsHydrated } from '@core/shared';
import { EmailListSend, EmailListStatus, EmailTemplateType, OptionsApi } from '@core/swagger-client';
import { Button, Close, Confirm, ContainerDefault, Edit, ErrorMessage, ErrorMessagePage, Heading, Input, LoaderPage, TabGroup } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { BackLink, Link } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { AppendRecipients } from './AppendRecipients';
import { SendEmailDialog } from './SendEmailDialog';
import { ViewSendTemplateDialog } from './ViewSendTemplateDialog';

const formatDate = (value?: string) => (value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-');

const templateTypeLabelKey = (templateType: EmailTemplateType) =>
    templateType === EmailTemplateType.WebinarEmailList ? 'EmailLists.TemplateType.WebinarEmailList' : 'EmailLists.TemplateType.EmailList';

const fullName = (firstName?: string | null, lastName?: string | null) => [firstName, lastName].filter(Boolean).join(' ');

export const EmailListDetailPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    const nav = useNavigate();
    const isHydrated = useIsHydrated();
    const queryClient = useQueryClient();

    const { id } = useParams({ strict: false });
    const listId = id ?? '';

    const detailQuery = OptionsApi.emailLists.getDetail(listId, { refetchInterval: 15000 });
    const { data: detail, isLoading, error } = useQuery(detailQuery);

    // Skip the fetch once the list is deleted — its recipients have been scrubbed, so there's nothing to show.
    const recipientsQuery = OptionsApi.emailLists.getRecipients(listId, {
        enabled: !!listId && detail?.status !== EmailListStatus.Deleted
    });
    const { data: recipients } = useQuery(recipientsQuery);

    const [activeTab, setActiveTab] = useState(0); // 0 = members, 1 = send history
    const [sendOpen, setSendOpen] = useState(false);
    const [viewSend, setViewSend] = useState<EmailListSend>();
    const [deleteOpen, setDeleteOpen] = useState(false);

    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [removeEmail, setRemoveEmail] = useState<string>();
    const [appendKey, setAppendKey] = useState(0);

    const removeMutation = useMutation(OptionsApi.emailLists.remove());
    const updateDescriptionMutation = useMutation(OptionsApi.emailLists.updateDescription());
    const removeRecipientMutation = useMutation(OptionsApi.emailLists.removeRecipient());

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading || !detail) {
        return <LoaderPage />;
    }

    const isDeleted = detail.status === EmailListStatus.Deleted;
    // "Used" = directly sent to OR used by a marketing campaign — either way the members are locked.
    const isUsed = detail.sends.length > 0 || detail.usedByCampaign;
    // Members are editable only while the list still exists and hasn't been sent to yet.
    const isMutable = !isDeleted && !isUsed;

    const invalidateList = () => {
        queryClient.invalidateQueries({ queryKey: detailQuery.queryKey });
        queryClient.invalidateQueries({ queryKey: recipientsQuery.queryKey });
    };

    const handleDelete = () => {
        removeMutation.mutate(listId, {
            onSuccess: () => nav({ to: '/manage/email-lists' })
        });
    };

    const startEditDescription = () => {
        setDescriptionDraft(detail.description);
        setEditingDescription(true);
    };

    const saveDescription = () => {
        updateDescriptionMutation.mutate(
            { id: listId, description: descriptionDraft.trim() },
            {
                onSuccess: () => {
                    setEditingDescription(false);
                    queryClient.invalidateQueries({ queryKey: detailQuery.queryKey });
                }
            }
        );
    };

    const confirmRemoveMember = () => {
        if (!removeEmail) {
            return;
        }
        removeRecipientMutation.mutate(
            { id: listId, email: removeEmail },
            {
                onSuccess: () => {
                    setRemoveEmail(undefined);
                    invalidateList();
                }
            }
        );
    };

    return (
        <ContainerDefault>
            <BackLink to="/manage/email-lists">{t('EmailLists.Button.BackToLists')}</BackLink>

            {editingDescription ? (
                <div className="flex flex-col gap-2 mb-6 max-w-lg">
                    <Input
                        data-testid="email-list-description-input"
                        label={t('EmailLists.Detail.Description.Label')}
                        value={descriptionDraft}
                        onChange={(event) => setDescriptionDraft(event.target.value)}
                    />
                    <div className="flex gap-2">
                        <Button
                            data-testid="email-list-description-save"
                            variant="success"
                            isLoading={updateDescriptionMutation.isPending}
                            disabled={!descriptionDraft.trim()}
                            onClick={saveDescription}
                        >
                            {t('Generic.Button.Save')}
                        </Button>
                        <Button variant="warning-outline" onClick={() => setEditingDescription(false)}>
                            {t('Generic.Button.Cancel')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-3 mb-4 lg:mb-6 2xl:mb-8">
                    <Heading variant="h1">{detail.description}</Heading>
                    {!isDeleted && (
                        <Button data-testid="email-list-description-edit" iconbtn onClick={startEditDescription}>
                            <Edit classNames="size-6 stroke-primary" />
                            <span className="sr-only">{t('EmailLists.Detail.Description.Edit')}</span>
                        </Button>
                    )}
                </div>
            )}

            {removeMutation.error && <ErrorMessage apiError={removeMutation.error} classNames="mb-4 max-w-4xl" />}
            {updateDescriptionMutation.error && <ErrorMessage apiError={updateDescriptionMutation.error} classNames="mb-4 max-w-4xl" />}

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-lg mb-8">
                <dt className="font-medium">{t('EmailLists.Detail.ImportedDate')}</dt>
                <dd>{formatDate(detail.importedDate)}</dd>
                <dt className="font-medium">{t('EmailLists.Detail.RecipientCount')}</dt>
                <dd>{detail.recipientCount}</dd>
                <dt className="font-medium">{t('EmailLists.Detail.Status')}</dt>
                <dd>{t(`EmailLists.Status.${EmailListStatus[detail.status]}`)}</dd>
            </dl>

            <div className="flex flex-wrap items-center gap-4 mb-10">
                <Button
                    data-testid="email-list-send-button"
                    variant="primary"
                    disabled={!isHydrated || detail.status !== EmailListStatus.Active}
                    onClick={() => setSendOpen(true)}
                >
                    {t('EmailLists.Detail.Button.Send')}
                </Button>
                <Button data-testid="email-list-delete-button" variant="warning" disabled={!isHydrated || isDeleted} onClick={() => setDeleteOpen(true)}>
                    {t('EmailLists.Detail.Button.Delete')}
                </Button>
            </div>

            <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                <TabGroup.TabList numTabs={2} activeTab={activeTab} setActiveTab={setActiveTab} classNames="border-b border-primary mb-6">
                    <TabGroup.Tab
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        index={0}
                        className="nav-tab-border"
                        activeClassName="nav-tab--active"
                        data-testid="email-list-tab-members"
                    >
                        {t('EmailLists.Detail.Tab.Members')}
                    </TabGroup.Tab>
                    <TabGroup.Tab
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        index={1}
                        className="nav-tab-border"
                        activeClassName="nav-tab--active"
                        data-testid="email-list-tab-history"
                    >
                        {t('EmailLists.Detail.Tab.SendHistory')}
                    </TabGroup.Tab>
                </TabGroup.TabList>

                <TabGroup.TabPanel activeTab={activeTab} setActiveTab={setActiveTab} index={0} className="" activeClassName="block" inactiveClassName="hidden">
                    {removeRecipientMutation.error && <ErrorMessage apiError={removeRecipientMutation.error} classNames="mb-4 max-w-4xl" />}

                    {isDeleted ? (
                        // A soft-deleted list has had its recipients scrubbed for GDPR — there's nothing to show.
                        <p className="text-gray-600">{t('EmailLists.Detail.Members.Locked')}</p>
                    ) : (
                        <>
                            {!recipients || recipients.length === 0 ? (
                                <p>{t('EmailLists.Detail.Members.Empty')}</p>
                            ) : (
                                <div className="table-responsive bg-white rounded-lg shadow-md">
                                    <table className="table table-striped mt-2" data-testid="email-list-members-table">
                                        <thead>
                                            <tr>
                                                <th>{t('EmailLists.Detail.Members.Header.Email')}</th>
                                                <th>{t('EmailLists.Detail.Members.Header.Name')}</th>
                                                <th>{t('EmailLists.Detail.Members.Header.Company')}</th>
                                                {isMutable && <th className="w-10"></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipients.map((recipient) => (
                                                <tr key={recipient.id}>
                                                    <td>{recipient.emailAddress}</td>
                                                    <td>{fullName(recipient.firstName, recipient.lastName)}</td>
                                                    <td>{recipient.companyName}</td>
                                                    {isMutable && (
                                                        <td className="text-right">
                                                            <Button
                                                                data-testid="email-list-member-remove"
                                                                onClick={() => setRemoveEmail(recipient.emailAddress)}
                                                                iconbtn
                                                                classNames="-my-4 translate-y-1.25"
                                                            >
                                                                <Close classNames="size-6 stroke-warning" />
                                                                <span className="sr-only">
                                                                    {t('EmailLists.Detail.Members.RemoveLabel', { email: recipient.emailAddress })}
                                                                </span>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {isMutable ? (
                                <div className="mt-10">
                                    <Heading variant="h2" classNames="mb-4 lg:mb-6">
                                        {t('EmailLists.Detail.Append.Heading')}
                                    </Heading>
                                    <p className="mb-4">{t('EmailLists.Detail.Append.Intro')}</p>
                                    <AppendRecipients
                                        key={appendKey}
                                        listId={listId}
                                        onComplete={() => {
                                            setAppendKey((value) => value + 1);
                                            invalidateList();
                                        }}
                                    />
                                </div>
                            ) : (
                                <p className="mt-6 text-gray-600">{t('EmailLists.Detail.Members.Locked')}</p>
                            )}
                        </>
                    )}
                </TabGroup.TabPanel>

                <TabGroup.TabPanel activeTab={activeTab} setActiveTab={setActiveTab} index={1} className="" activeClassName="block" inactiveClassName="hidden">
                    {detail.sends.length === 0 && detail.campaigns.length === 0 && <p>{t('EmailLists.Detail.SendLog.Empty')}</p>}

                    {detail.sends.length > 0 && (
                        <div className="table-responsive bg-white rounded-lg shadow-md">
                            <table className="table table-striped mt-2" data-testid="email-list-sends-table">
                                <thead>
                                    <tr>
                                        <th>{t('EmailLists.Detail.SendLog.TemplateType')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.SentAt')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.RecipientCount')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.SentCount')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.FailedCount')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.BlacklistedCount')}</th>
                                        <th>{t('EmailLists.Detail.SendLog.AlreadyInterestedCount')}</th>
                                        <th className="text-right">{t('EmailLists.Detail.SendLog.Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.sends.map((send) => (
                                        <tr key={send.sendId}>
                                            <td>
                                                {t(templateTypeLabelKey(send.templateType))}
                                                {send.webinarTitle && <span className="block text-sm text-gray-600">{send.webinarTitle}</span>}
                                            </td>
                                            <td>{formatDate(send.sentAt)}</td>
                                            <td>{send.recipientCount}</td>
                                            <td>{send.sentCount}</td>
                                            <td>{send.failedCount}</td>
                                            <td>{send.blacklistedCount}</td>
                                            <td>{send.alreadyInterestedCount}</td>
                                            <td className="text-right">
                                                <Button variant="secondary-outline" data-testid="email-list-send-view" onClick={() => setViewSend(send)}>
                                                    {t('EmailLists.Detail.SendLog.ViewTemplate')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {detail.campaigns.length > 0 && (
                        <div className={detail.sends.length > 0 ? 'mt-10' : ''}>
                            <Heading variant="h2" classNames="mb-4 lg:mb-6">
                                {t('EmailLists.Detail.Campaigns.Heading')}
                            </Heading>
                            <div className="table-responsive bg-white rounded-lg shadow-md">
                                <table className="table table-striped mt-2" data-testid="email-list-campaigns-table">
                                    <thead>
                                        <tr>
                                            <th>{t('EmailLists.Detail.Campaigns.Name')}</th>
                                            <th>{t('EmailLists.Detail.Campaigns.UsedAt')}</th>
                                            <th className="w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.campaigns.map((campaign) => (
                                            <tr key={campaign.campaignId}>
                                                <td>{campaign.name}</td>
                                                <td>{formatDate(campaign.usedAt)}</td>
                                                <td className="text-right">
                                                    <Link
                                                        data-testid={`email-list-campaign-link-${campaign.campaignId}`}
                                                        to="/$webinarId/marketing/$id"
                                                        params={{ webinarId: campaign.webinarId, id: campaign.campaignId }}
                                                        variant="link"
                                                    >
                                                        {t('EmailLists.Detail.Campaigns.View')}
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </TabGroup.TabPanel>
            </TabGroup>

            <SendEmailDialog
                open={sendOpen}
                listId={listId}
                existingSends={detail.sends}
                onClose={() => setSendOpen(false)}
                onSent={() => {
                    setSendOpen(false);
                    queryClient.invalidateQueries({ queryKey: detailQuery.queryKey });
                }}
            />

            <ViewSendTemplateDialog listId={listId} open={!!viewSend} send={viewSend} onClose={() => setViewSend(undefined)} />

            <Confirm
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                title={t('EmailLists.Delete.Title')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('EmailLists.Delete.Confirm')}
                confirmTestId="email-list-delete-confirm"
            >
                <p>{isUsed ? t('EmailLists.Delete.Body.Used') : t('EmailLists.Delete.Body.Unused')}</p>
            </Confirm>

            <Confirm
                open={!!removeEmail}
                onClose={() => setRemoveEmail(undefined)}
                onConfirm={confirmRemoveMember}
                title={t('EmailLists.Detail.Members.RemoveTitle')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('EmailLists.Detail.Members.RemoveConfirm')}
                confirmTestId="email-list-member-remove-confirm"
            >
                <p>{t('EmailLists.Detail.Members.RemoveBody', { email: removeEmail })}</p>
            </Confirm>
        </ContainerDefault>
    );
};
